import { FaSpinner } from 'react-icons/fa';
import accounting from 'accounting';
import { Button, Textarea, Box, HStack, Input, Heading, Grid } from '@chakra-ui/react';
import { withFormik } from 'formik';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import sumBy from 'lodash/sumBy';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import Loading from '../basic/Loading';
import { sumOrdersByWeightType } from './helpers';
import './InvoiceEmailModal.scss';
import PropTypes from 'prop-types';
import { formatMonetaryValue } from '../../functions';
import { DialogRoot, DialogContent, DialogHeader, DialogBody, DialogFooter } from '@/components/ui/dialog';
import { Alert } from '../ui/alert';
import { Field } from '@/components/ui/field';
import { Switch } from '@/components/ui/switch';

function initializeForm(props) {
  const initialValues = get(props, 'initialValues') || {};
  return {
    body: initialValues.body || '',
    subject: initialValues.subject || '',
  };
}

const formikEnhancer = withFormik({
  validationSchema: Yup.object().shape({
    subject: Yup.string().required('Required'),
    body: Yup.string().required('Required'),
  }),
  handleSubmit: (values, form) => {
    form.props.onSubmit({
      ...values,
      poList: form.props.orderList,
      batch: form.props.isBatch,
    });
    form.setSubmitting(false);
  },
  mapPropsToValues: initializeForm,
});
const propTypes = {
  error: PropTypes.string,
  user: PropTypes.shape({
    email: PropTypes.string,
  }),
  onCancel: PropTypes.func.isRequired,
  onBatchChange: PropTypes.func,
  isVisible: PropTypes.bool,
  isDownloadingAllAttachments: PropTypes.bool,
  isBatch: PropTypes.bool,
  orderList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      grinder_po_number: PropTypes.string.isRequired,
      sell_unit_of_measure: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }).isRequired,
      sell_currency: PropTypes.string,
      pricing_details: PropTypes.shape({
        total_sell_price: PropTypes.number,
      }),
      grinder: PropTypes.shape({
        destination_country: PropTypes.string,
      }),
    })
  ),
  onAttachmentClick: PropTypes.func,
  onDownloadAllClick: PropTypes.func,
  onGenerateSummary: PropTypes.func,
  pendingPDFDownloadList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    })
  ),
  // Provided By Formik:
  setFieldValue: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func,
  values: PropTypes.shape({
    subject: PropTypes.string,
    body: PropTypes.string,
  }),
  isManualPricing: PropTypes.bool,
  packageWeights: PropTypes.arrayOf(PropTypes.Object),
  recipientsComponent: PropTypes.element,
  isSendingInvoices: PropTypes.bool,
};

const InvoiceEmailModal = props => {
  const {
    error,
    user,
    onCancel,
    onBatchChange,
    isVisible,
    isDownloadingAllAttachments,
    isBatch,
    orderList,
    onAttachmentClick,
    onDownloadAllClick,
    onGenerateSummary,
    pendingPDFDownloadList,
    // Provided By Formik:
    setFieldValue,
    handleSubmit,
    values,
    isManualPricing,
    packageWeights,
    recipientsComponent,
    isSendingInvoices,
  } = props;
  const [emailContent, setEmailContent] = useState({ body: '', subject: '' });
  useEffect(() => {
    const { body, subject } = initializeForm(props);
    setEmailContent({ body, subject });
  }, [isVisible]);
  const invoiceCount = orderList ? orderList.length : 0;
  // Weight Total Groups
  // Note: Purposely choosing to use line quantity values instead of pricing details.total_sell_quantity.
  // as there is a potential for pricing_details to not be returned if required fields are missing.
  const weightRowGroups = groupBy(orderList, 'sell_unit_of_measure.name');
  const sortedWeightKeys = orderBy(Object.keys(weightRowGroups), key => key);
  const weightTotalList = sortedWeightKeys.map(weightKey => {
    const rows = weightRowGroups[weightKey];
    return {
      unit: weightKey,
      amount: sumOrdersByWeightType(rows, packageWeights),
    };
  });

  // Dollar Total Groups
  const currencyRowGroups = groupBy(orderList, 'sell_currency');
  const sortedCurrencyKeys = orderBy(Object.keys(currencyRowGroups), key => key.toLowerCase());
  const dollarTotalList = sortedCurrencyKeys.map(currencyKey => {
    const rows = currencyRowGroups[currencyKey];
    return {
      unit: currencyKey === 'null' ? 'USD' : currencyKey,
      amount: sumBy(rows, 'pricing_details.total_sell_price'),
    };
  });

  const hasDomesticGrinder = !!orderList.find(order =>
    ['New Zealand', 'Australia'].includes(get(order, 'grinder.destination_country'))
  );

  return (
    <DialogRoot open={isVisible} onClose={onCancel} motionPreset="slideInBottom">
      <DialogContent maxW="90%">
        <DialogHeader>
          <Box align="center" fontSize="2xl">
            Email Preview
          </Box>
        </DialogHeader>
        <DialogBody>
          <form onSubmit={handleSubmit}>
            <Box>
              {/* <!-- ERROR --> */}
              {error && <Alert status="error">{error}</Alert>}
              {/* <!-- TEXT --> */}
              <Box mb="30px" fontSize="20px" align="center">
                Invoices have been generated and attached to this auto-generated email draft.
              </Box>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <Box md="9">
                  {/* <!-- TOTALS --> */}
                  <HStack gap="30px">
                    <HStack letterSpacing="wide" fontSize="md" color="black" bg="neutral.9" borderRadius="md" p={5}>
                      <Box fontSize="xl" fontWeight="400">
                        {invoiceCount}
                      </Box>
                      <Box>no. Invoices</Box>
                    </HStack>
                    <HStack letterSpacing="wide" color="black" bg="neutral.9" borderRadius="md" p={5}>
                      {weightTotalList.map((total, idx) => (
                        <Box fontSize="xl" fontWeight="400" key={`weight-total-${idx}`}>
                          {accounting.formatNumber(total.amount, 2, ',')}
                          <span>{total.unit}</span>
                        </Box>
                      ))}

                      <Box fontSize="15px">Total Weight(s)</Box>
                    </HStack>
                    <HStack letterSpacing="wide" color="black" bg="neutral.9" borderRadius="md" p={5}>
                      {dollarTotalList.map((total, idx) => (
                        <Box fontSize="xl" fontWeight="400" key={`dollar-total-${idx}`}>
                          {formatMonetaryValue(total.unit, total.amount, {
                            notation: 'standard',
                            minimumFractionDigits: 2,
                          })}
                        </Box>
                      ))}

                      <Box fontSize="15px">Total Invoice Amount(s)</Box>
                    </HStack>
                  </HStack>
                  {/* <!-- SUBHEADER --> */}
                  <Heading size="lg" mt="20px" mb="20px">
                    Email Preview
                  </Heading>
                  {/* <!-- SUBJECT --> */}

                  <Input
                    name="subject"
                    mb="20px"
                    placeholder="Subject"
                    value={emailContent.subject}
                    onChange={e => {
                      const value = get(e, 'target.value', '');
                      setFieldValue('subject', value);
                      setEmailContent({ ...emailContent, subject: value });
                    }}
                  />
                  {/* <!-- BODY --> */}
                  <Textarea
                    name="body"
                    value={emailContent.body}
                    placeholder="Body"
                    rows={12}
                    onChange={e => {
                      const value = get(e, 'target.value', '');
                      setFieldValue('body', value);
                      setEmailContent({ ...emailContent, body: value });
                    }}
                  />
                  {/* <!-- RECIPIENTS --> */}
                  {recipientsComponent}
                </Box>
                <Box md="3">
                  <Box>
                    <Box>
                      {/* <!-- SUBHEADER --> */}
                      <Heading size="md">Attachments / Invoices</Heading>
                      {/* <!-- ATTACHMENTS --> */}
                      <Box>
                        {orderList?.map((order, idx) => {
                          const isLoading = pendingPDFDownloadList?.map(po => po.id).includes(order.id);
                          return (
                            <Button
                              className={`invoice-email-modal__attachment ${
                                !isDownloadingAllAttachments && isLoading && 'invoice-email-modal__attachment--loading'
                              }`}
                              key={`attachment-${idx}`}
                              onClick={() => onAttachmentClick(order)}
                              marginTop="6px"
                            >
                              <span>PO#: {order.grinder_po_number}</span>
                              {!isDownloadingAllAttachments && isLoading && <FaSpinner />}
                            </Button>
                          );
                        })}
                        {hasDomesticGrinder && !isManualPricing && (
                          <Button onClick={() => onGenerateSummary(orderList)} m={5}>
                            Invoice Breakdown
                          </Button>
                        )}
                      </Box>
                      {/* <!-- DOWNLOAD LINK --> */}
                      {isDownloadingAllAttachments ? (
                        <Loading />
                      ) : (
                        <Button
                          variant="outline"
                          onClick={() => onDownloadAllClick(orderList)}
                          colorScheme="actionSecondary"
                          margin="20px 0"
                        >
                          Download All
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Box>
          </form>
        </DialogBody>

        <DialogFooter>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ padding: '10px' }}>{isSendingInvoices && <FaSpinner />}</div>
            <div>
              <Field label="Seperately" htmlFor="isBatch" mr="5px" display="inline">
                <Switch
                  size="lg"
                  colorScheme="actionPrimary"
                  style={{ margin: '4px' }}
                  id="isBatch"
                  defaultChecked={isBatch}
                  onChange={e => {
                    const value = e.nativeEvent.target.checked;
                    onBatchChange(value);
                  }}
                />
              </Field>
              <Field label="Batch" htmlFor="isBatch" ml="5px" display="inline">
                <Button
                  type="submit"
                  colorScheme="actionPrimary"
                  width="100px"
                  onClick={handleSubmit}
                  mr={3}
                  disabled={isSendingInvoices}
                >
                  Email
                </Button>
              </Field>
            </div>
            <div>
              <Button colorScheme="actionPrimary" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

InvoiceEmailModal.prototype = propTypes;

export default formikEnhancer(InvoiceEmailModal);
